/**
 * When the tracker offers "Switch to DTF / DTG" (one garment, BLU-1412, or a
 * whole order), and what the dialogs and the station switch say about the
 * answer.
 *
 * Entry point only. The server re-checks everything under a row lock (print
 * timestamps, films, the order's state, the owner-floor fence); this predicate
 * keeps the menu from offering a switch the server is certain to refuse, from
 * what the garment payload already carries.
 *
 * The switch is in place (BLU-1509): the garment keeps its status, its label
 * and its place in the batch; only its print technology changes. Nothing is
 * cancelled and no redo is made.
 */
import type { RouterOutputs } from '@/lib/trpc';
import {
  otherPrintTechnology,
  type PrintTechnology,
} from '@/features/production-orders/utils/print-tech-routing';

/** `prod_garment_units.redo_reason` on a switched replacement (backend PRINT_TECHNOLOGY_SWITCH_REASON). */
export const PRINT_TECHNOLOGY_SWITCH_REASON = 'print_technology_switch';

/**
 * ⚠ KEEP IN LOCKSTEP with `SWITCHABLE_GARMENT_STATUSES` in
 * apps/backend/src/modules/production-orders/print-technology-switch.ts, which
 * is itself held to the RPC's literal. Nothing printed and no film at the RIP.
 */
export const SWITCHABLE_GARMENT_STATUSES = ['PENDING', 'LABEL_PRINTED', 'PICKED'] as const;

const SWITCHABLE: ReadonlySet<string> = new Set(SWITCHABLE_GARMENT_STATUSES);

/** The slice of a tracker garment the predicate reads. */
export interface SwitchableGarment {
  status: string;
  productionMethod: string;
  /** Optional so a stale cached payload offers nothing rather than a wrong switch. */
  printTechnology?: PrintTechnology;
  /**
   * The server refuses an unbatched garment (`not_batched`), which is exactly
   * what a switch's own replacement is. Optional so a stale cached payload
   * offers nothing rather than a switch the server refuses.
   */
  batchId?: string | null;
}

/**
 * The switch the menu would offer for this garment, or null when the item
 * stays hidden. The caller has already checked `production_settings.manage`
 * and that the selected floor is the org's own.
 */
export function printTechnologySwitchFor(
  garment: SwitchableGarment,
  { dtfEnabled }: { dtfEnabled: boolean },
): { from: PrintTechnology; to: PrintTechnology } | null {
  const from = garment.printTechnology;
  if (!from) return null;
  if (!garment.batchId) return null;
  // The payload maps a BLANK- wrapper to PAP, so this covers both refusals.
  if (garment.productionMethod !== 'POD') return null;
  if (!SWITCHABLE.has(garment.status)) return null;
  const to = otherPrintTechnology(from);
  if (to === 'DTF' && !dtfEnabled) return null;
  return { from, to };
}

/**
 * BLU-1509: the directions an order switch is offered in, DTG first: "to DTG"
 * when any loaded garment can go DTF to DTG, "to DTF" when any can go DTG to
 * DTF (only when DTF is on). Same per-garment predicate as the garment menu;
 * the server re-checks every garment of the order, in every batch.
 */
export function orderSwitchDirections(garments: ReadonlyArray<SwitchableGarment>, dtfEnabled: boolean): PrintTechnology[] {
  const targets = new Set<PrintTechnology>();
  for (const g of garments) {
    const techSwitch = printTechnologySwitchFor(g, { dtfEnabled });
    if (techSwitch) targets.add(techSwitch.to);
  }
  return (['DTG', 'DTF'] as const).filter((to) => targets.has(to));
}

/** A label is printed once the garment leaves PENDING, so the paper says the old technology. */
export function hasPrintedLabel(status: string): boolean {
  return status !== 'PENDING';
}

type GarmentSwitchResult = RouterOutputs['productionOrders']['switchGarmentPrintTechnology'];
type OrderSwitchResult = RouterOutputs['productionOrders']['switchOrderPrintTechnology'];
type StationSwitchResult =
  | RouterOutputs['printStation']['switchToDtg']
  | RouterOutputs['filmPrint']['switchToDtf'];

/** Every refusal code any of the four switch procedures can answer with. */
export type PrintTechnologySwitchRefusal =
  | Extract<GarmentSwitchResult, { ok: false }>['reason']
  | OrderSwitchResult['refused'][number]['errorCode']
  | Extract<StationSwitchResult, { ok: false }>['errorCode'];

/** Literal keys (not a template string) so check:i18n can see them. */
export const SWITCH_REFUSAL_KEYS: Record<PrintTechnologySwitchRefusal, string> = {
  not_found: 'tracker.printTechSwitch.refusal.notFound',
  already_switched: 'tracker.printTechSwitch.refusal.alreadySwitched',
  not_switchable: 'tracker.printTechSwitch.refusal.notSwitchable',
  already_printed: 'tracker.printTechSwitch.refusal.alreadyPrinted',
  same_technology: 'tracker.printTechSwitch.refusal.sameTechnology',
  not_pod: 'tracker.printTechSwitch.refusal.notPod',
  not_batched: 'tracker.printTechSwitch.refusal.notBatched',
  not_customer_batch: 'tracker.printTechSwitch.refusal.notCustomerBatch',
  order_not_in_production: 'tracker.printTechSwitch.refusal.orderNotInProduction',
  film_exists: 'tracker.printTechSwitch.refusal.filmExists',
  dtf_disabled: 'tracker.printTechSwitch.refusal.dtfDisabled',
  busy: 'tracker.printTechSwitch.refusal.busy',
};

const REFUSAL_KEY_BY_CODE: Partial<Record<string, string>> = SWITCH_REFUSAL_KEYS;

/**
 * The copy key for a refusal code, or null for a code this build does not
 * know (a newer backend during a deploy). The caller then shows only its
 * generic line, never a raw code.
 */
export function switchRefusalKey(code: string | null | undefined): string | null {
  return (code && REFUSAL_KEY_BY_CODE[code]) || null;
}

/**
 * `busy`: another request held the garment's lock. Nothing is wrong with the
 * garment, so the same person can simply try again.
 */
export function isRetryableSwitchRefusal(code: string | null | undefined): boolean {
  return code === 'busy';
}
